/**
 * Grafana Faro: init, user context, pushEvent, reportFaroError, getAppName.
 * Metadata as context only (no high-cardinality labels). User context only after login.
 */

import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import type { FaroUserContext } from './types';
import { OBSERVABILITY_ENABLED } from './constants';
import { getDeviceMetadata } from './device.service';
import { getSessionId } from './session.service';
import { DEFAULT_APP_NAME } from './constants';

const collectUrl =
  typeof process !== 'undefined' && process.env?.REACT_APP_FARO_COLLECT_URL
    ? String(process.env.REACT_APP_FARO_COLLECT_URL).trim()
    : '';

let faroInstance: ReturnType<typeof initializeFaro> | null = null;
let faroUserContext: FaroUserContext | null = null;

export function getAppName(): string {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_APP_NAME) {
    return String(process.env.REACT_APP_APP_NAME).trim() || DEFAULT_APP_NAME;
  }
  return DEFAULT_APP_NAME;
}

function getAppVersion(): string {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_VERSION) {
    return String(process.env.REACT_APP_VERSION).trim();
  }
  return '0.0.0';
}

function getEnvironment(): string {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_ENV) {
    return String(process.env.REACT_APP_ENV).trim();
  }
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    return process.env.NODE_ENV;
  }
  return 'development';
}

export function isObservabilityEnabled(): boolean {
  return OBSERVABILITY_ENABLED && collectUrl.length > 0;
}

function getBaseEventAttributes(): Record<string, string | number | undefined> {
  const meta = getDeviceMetadata();
  const sessionId = getSessionId();
  return {
    app_name: getAppName(),
    device_id: meta.device_id,
    session_id: sessionId,
    device_type: meta.device_type,
    os_version: meta.os_version,
    browser: `${meta.browser_name}/${meta.browser_version}`,
    environment: getEnvironment(),
    app_version: getAppVersion(),
    ...(faroUserContext?.user_id != null ? { user_id: faroUserContext.user_id } : {}),
    ...(faroUserContext?.tenant_id != null ? { tenant_id: faroUserContext.tenant_id } : {}),
  };
}

export function initFaro(): void {
  if (!OBSERVABILITY_ENABLED || !collectUrl) {
    return;
  }
  if (faroInstance) {
    return;
  }
  try {
    faroInstance = initializeFaro({
      url: collectUrl,
      app: {
        name: getAppName(),
        version: getAppVersion(),
      },
      instrumentations: [...getWebInstrumentations({ captureConsole: true })],
      beforeSend: (item) => {
        // Optional: attach device/session as meta to every beacon (context, not labels)
        return item;
      },
    });
  } catch {
    faroInstance = null;
  }
}

export function setFaroUserContext(ctx: FaroUserContext): void {
  faroUserContext = ctx;
}

export function clearFaroUserContext(): void {
  faroUserContext = null;
}

function toEventAttributes(obj: Record<string, string | number | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null) out[k] = String(v);
  }
  return out;
}

export function pushFaroEvent(
  name: string,
  attributes?: Record<string, string | number | undefined>
): void {
  if (!faroInstance?.api?.pushEvent) return;
  const base = toEventAttributes(getBaseEventAttributes());
  const attrs = attributes ? { ...base, ...toEventAttributes(attributes) } : base;
  faroInstance.api.pushEvent(name, attrs);
}

export function reportFaroError(
  error: Error,
  context?: Record<string, string | number | undefined>
): void {
  if (!faroInstance?.api?.pushError) return;
  faroInstance.api.pushError(error);
  if (context && Object.keys(context).length > 0 && faroInstance.api.pushEvent) {
    faroInstance.api.pushEvent('error_reported', {
      ...getBaseEventAttributes(),
      message: error.message,
      ...context,
    });
  }
}

export function getFaroInstance(): ReturnType<typeof initializeFaro> | null {
  return faroInstance;
}
